import { useContext, useState } from "react";
import { Input } from "/src/modern/components/ui/input";
import { Button } from "/src/modern/components/ui/button";
import { Badge } from "/src/modern/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "/src/modern/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "/src/modern/components/ui/tooltip";
import AuthContext from "@/react/context/authContext.jsx";

function icon(iconClass, extraStyle = {}) {
  return <i className={`${iconClass}`} style={extraStyle} />;
}

export function SearchFunctionality({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  onClearSearch,
}) {
  const { appRolesRef = [] } = useContext(AuthContext);
  const swivuUserRoles = appRolesRef.current.filter((appRole) => !([6, 7].includes(appRole.id)));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const activeFilterCount = filters.boardTypes.length + filters.roles.length + filters.workforceStatus.length + filters.categories.length;

  const handleFilterChange = (filterType, value, checked) => {
    const newFilters = { ...filters };
    if (checked) {
      newFilters[filterType] = [...newFilters[filterType], value];
    } else {
      newFilters[filterType] = newFilters[filterType].filter((item) => item !== value);
    }
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      boardTypes: [],
      roles: [],
      workforceStatus: [],
      categories: [],
    });
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4">
        {/* Search Input */}
        <div className="flex gap-swivu-horizontal">
          <div className="relative flex-1">
            <i
              className="fa fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground text-swivu-icon"
            />
            <Input
              placeholder="Search boards and templates..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-10 dark:bg-[#242429] outline-none !shadow-none !ring-0 !ring-offset-0 focus:!shadow-none"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClearSearch}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              >
                {icon("fa fa-times")}
              </Button>
            )}
          </div>

          {/* Filter Dropdown */}
          <DropdownMenu open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="relative dark:bg-[#242429]">
                {icon("fa fa-filter")}
                Filters
                {activeFilterCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-2 h-5 w-5 p-0 text-xs text-center leading-5"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
                <i className="fa fa-chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 dark:bg-[#242429]" align="end">
              <DropdownMenuLabel className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  Filter Options
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <i
                        className="fa fa-info-circle text-muted-foreground"
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-swivu-sm">
                        All selected filters must match (AND logic)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="h-6 text-swivu-sm"
                  >
                    Clear All
                  </Button>
                )}
              </DropdownMenuLabel>

              <DropdownMenuSeparator />
              {/* Board Type */}
              <DropdownMenuLabel className="text-xs uppercase text-muted-foreground tracking-wider">Board Type</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={filters.boardTypes.includes("created")}
                onCheckedChange={(checked) =>
                  handleFilterChange("boardTypes", "created", checked)
                }
              >
                {icon("fa fa-crown", { color: "#2563eb", marginRight: 8 })}
                Boards I Created
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.boardTypes.includes("invited")}
                onCheckedChange={(checked) =>
                  handleFilterChange("boardTypes", "invited", checked)
                }
              >
                {icon("fa fa-user-plus", { color: "#16a34a", marginRight: 8 })}
                Boards I'm Invited To
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.boardTypes.includes("template")}
                onCheckedChange={(checked) =>
                  handleFilterChange("boardTypes", "template", checked)
                }
              >
                {icon("fa fa-magic", { color: "#7c3aed", marginRight: 8 })}
                Templates
              </DropdownMenuCheckboxItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs uppercase text-muted-foreground tracking-wider">My Role</DropdownMenuLabel>
              {swivuUserRoles.map((role) => (
                <DropdownMenuCheckboxItem
                  key={role.id}
                  checked={filters.roles.includes(role.name)}
                  onCheckedChange={(checked) =>
                    handleFilterChange("roles", role.name, checked)
                  }
                >
                  {role.name}
                </DropdownMenuCheckboxItem>
              ))}

              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs uppercase text-muted-foreground tracking-wider">Workforce Status</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={filters.workforceStatus.includes("active")}
                onCheckedChange={(checked) =>
                  handleFilterChange("workforceStatus", "active", checked)
                }
              >
                Active Workforce
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filters.workforceStatus.includes("inactive")}
                onCheckedChange={(checked) =>
                  handleFilterChange("workforceStatus", "inactive", checked)
                }
              >
                Inactive Workforce
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>

          </DropdownMenu>
        </div>
      </div>
    </TooltipProvider>
  );
}
